import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from '../hooks/use-toast';
import { BACKEND_URL } from '../config/canonical';
import { ArrowLeft, RefreshCw, Save, Car } from 'lucide-react';

/**
 * Phase B — Routed Vehicle Add/Edit page.
 * Routes: /cases/:caseId/vehicles/new  and  /cases/:caseId/vehicles/:vehicleId/edit
 */
const VehicleFormPage = () => {
  const { caseId, vehicleId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem('nexus_token');
  const isEdit = Boolean(vehicleId);

  const empty = {
    license_plate: '', state: '', make: '', model: '', year: '', color: '',
    registered_owner: '', notes: '',
  };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!isEdit) return;
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/vehicles/${vehicleId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const v = await res.json();
      setForm({ ...empty, ...v });
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [vehicleId, isEdit, token]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!form.license_plate) {
      toast({ title: 'Error', description: 'License plate is required', variant: 'destructive' });
      return;
    }
    setBusy(true);
    try {
      const url = isEdit ? `${BACKEND_URL}/api/vehicles/${vehicleId}` : `${BACKEND_URL}/api/cases/${caseId}/vehicles`;
      const method = isEdit ? 'PATCH' : 'POST';
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(form),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      toast({ title: 'Success', description: isEdit ? 'Vehicle updated' : 'Vehicle added' });
      navigate(`/cases/${caseId}?tab=vehicles`);
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setBusy(false);
    }
  };

  const field = (label, key, opts = {}) => (
    <div>
      <Label className="text-slate-300">{label}</Label>
      {opts.textarea ? (
        <Textarea value={form[key] || ''} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
          className="bg-slate-800 border-slate-600 text-white" rows={opts.rows || 3} />
      ) : (
        <Input value={form[key] || ''} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
          type={opts.type || 'text'} className="bg-slate-800 border-slate-600 text-white"
          placeholder={opts.placeholder} data-testid={opts.testid} />
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-300 p-6">
        <RefreshCw className="w-4 h-4 animate-spin" /> Loading…
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-4" data-testid="vehicle-form-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="outline" className="border-slate-600 !text-slate-200 hover:bg-slate-800"
          onClick={() => navigate(`/cases/${caseId}?tab=vehicles`)} data-testid="vehicle-form-back-btn">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to Case
        </Button>
        <h1 className="text-2xl font-bold text-white">{isEdit ? 'Edit Vehicle' : 'Add Vehicle'}</h1>
      </div>

      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {field('License Plate *', 'license_plate', { testid: 'vehicle-plate-input', placeholder: 'ABC-1234' })}
            {field('State', 'state', { placeholder: 'TX' })}
            {field('Make', 'make', { placeholder: 'Ford' })}
            {field('Model', 'model', { placeholder: 'F-150' })}
            {field('Year', 'year', { placeholder: '2024' })}
            {field('Color', 'color', { placeholder: 'Black' })}
            {field('Registered Owner', 'registered_owner')}
          </div>
          {field('Notes', 'notes', { textarea: true })}
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-700">
            <Button variant="outline" onClick={() => navigate(`/cases/${caseId}?tab=vehicles`)}
              className="border-slate-600 !text-slate-200">Cancel</Button>
            <Button onClick={submit} disabled={busy} className="bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-60"
              data-testid="save-vehicle-btn">
              {busy ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" />{isEdit ? 'Saving…' : 'Adding…'}</>
                : <>{isEdit ? <><Save className="w-4 h-4 mr-2" />Save Changes</> : <><Car className="w-4 h-4 mr-2" />Add Vehicle</>}</>}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default VehicleFormPage;
